import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Plus, ShieldCheck, Warning, CheckCircle, Trash, Play, XCircle, CaretRight, ArrowsClockwise, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import InfoTooltip from '../components/InfoTooltip';
import EmptyState from '../components/EmptyState';

export default function SodPage() {
  const [policies, setPolicies] = useState([]);
  const [violations, setViolations] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [roles, setRoles] = useState([]);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'role',
    severity: 'high',
    enforcement: 'warn',
    conflict_items: []
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    severity: 'high',
    enforcement: 'warn',
    enabled: true,
    conflict_items: []
  });
  const [conflictPair, setConflictPair] = useState({ item1: '', item2: '' });
  const [editConflictPair, setEditConflictPair] = useState({ item1: '', item2: '' });
  const [resolveForm, setResolveForm] = useState({ resolution: 'accepted', notes: '' });

  const loadPolicies = useCallback(async () => {
    try {
      const res = await API.get('/sod/policies');
      setPolicies(res.data.items);
    } catch (err) { toast.error('Failed to load policies'); }
  }, []);

  const loadViolations = useCallback(async () => {
    try {
      const res = await API.get('/sod/violations', { params: { status: 'open' } });
      setViolations(res.data.items);
    } catch (err) { toast.error('Failed to load violations'); }
  }, []);

  useEffect(() => {
    loadPolicies();
    loadViolations();
    API.get('/roles', { params: { limit: 200 } }).then(r => setRoles(r.data.items)).catch(() => {});
  }, [loadPolicies, loadViolations]);

  const handleCreate = async () => {
    if (!form.name || form.conflict_items.length === 0) {
      toast.error('Name and at least one conflict pair are required');
      return;
    }
    try {
      await API.post('/sod/policies', form);
      toast.success('Policy created');
      setShowCreateDialog(false);
      setForm({ name: '', description: '', type: 'role', severity: 'high', enforcement: 'warn', conflict_items: [] });
      loadPolicies();
      loadViolations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create policy'); }
  };

  const addConflictPair = () => {
    if (!conflictPair.item1 || !conflictPair.item2) {
      toast.error('Select both roles for the conflict');
      return;
    }
    if (conflictPair.item1 === conflictPair.item2) {
      toast.error('Cannot conflict a role with itself');
      return;
    }
    setForm({
      ...form,
      conflict_items: [...form.conflict_items, [conflictPair.item1, conflictPair.item2]]
    });
    setConflictPair({ item1: '', item2: '' });
  };

  const removeConflictPair = (index) => {
    setForm({
      ...form,
      conflict_items: form.conflict_items.filter((_, i) => i !== index)
    });
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Delete this policy and all its violations?')) return;
    try {
      await API.delete(`/sod/policies/${policyId}`);
      toast.success('Policy deleted');
      loadPolicies();
      loadViolations();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const openEditDialog = (policy) => {
    setSelectedPolicy(policy);
    setEditForm({
      name: policy.name,
      description: policy.description || '',
      severity: policy.severity,
      enforcement: policy.enforcement,
      enabled: policy.enabled,
      conflict_items: [...policy.conflict_items]
    });
    setEditConflictPair({ item1: '', item2: '' });
    setShowEditDialog(true);
  };

  const addEditConflictPair = () => {
    if (!editConflictPair.item1 || !editConflictPair.item2) {
      toast.error('Select both roles for the conflict');
      return;
    }
    if (editConflictPair.item1 === editConflictPair.item2) {
      toast.error('Cannot conflict a role with itself');
      return;
    }
    // Check if pair already exists
    const exists = editForm.conflict_items.some(
      pair => (pair[0] === editConflictPair.item1 && pair[1] === editConflictPair.item2) ||
              (pair[0] === editConflictPair.item2 && pair[1] === editConflictPair.item1)
    );
    if (exists) {
      toast.error('This conflict pair already exists');
      return;
    }
    setEditForm({
      ...editForm,
      conflict_items: [...editForm.conflict_items, [editConflictPair.item1, editConflictPair.item2]]
    });
    setEditConflictPair({ item1: '', item2: '' });
  };

  const removeEditConflictPair = (index) => {
    setEditForm({
      ...editForm,
      conflict_items: editForm.conflict_items.filter((_, i) => i !== index)
    });
  };

  const handleEdit = async () => {
    if (!editForm.name) {
      toast.error('Policy name is required');
      return;
    }
    if (editForm.conflict_items.length === 0) {
      toast.error('At least one conflict pair is required');
      return;
    }
    try {
      await API.put(`/sod/policies/${selectedPolicy.id}`, editForm);
      toast.success('Policy updated');
      setShowEditDialog(false);
      loadPolicies();
      loadViolations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update policy'); }
  };

  const togglePolicyEnabled = async (policy) => {
    try {
      await API.put(`/sod/policies/${policy.id}`, { enabled: !policy.enabled });
      toast.success(policy.enabled ? 'Policy disabled' : 'Policy enabled');
      loadPolicies();
    } catch (err) { toast.error('Failed to update policy'); }
  };

  const handleCheckAll = async () => {
    setChecking(true);
    try {
      const res = await API.post('/sod/check-all');
      toast.success(`Checked ${res.data.policies_checked} policies, found ${res.data.new_violations_found} new violations`);
      loadPolicies();
      loadViolations();
    } catch (err) { toast.error('Check failed'); }
    finally { setChecking(false); }
  };

  const openResolveDialog = (violation) => {
    setSelectedViolation(violation);
    setResolveForm({ resolution: 'accepted', notes: '' });
    setShowResolveDialog(true);
  };

  const handleResolve = async () => {
    try {
      await API.put(`/sod/violations/${selectedViolation.id}/resolve`, resolveForm);
      toast.success('Violation resolved');
      setShowResolveDialog(false);
      loadViolations();
      loadPolicies();
    } catch (err) { toast.error('Failed to resolve'); }
  };

  const severityColor = (s) => {
    if (s === 'high') return 'bg-red-100 text-red-700';
    if (s === 'medium') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-navy-600';
  };

  const getRoleName = (id) => {
    const role = roles.find(r => r.id === id);
    return role ? role.name : id;
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="sod-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Separation of Duties <InfoTooltip concept="sod" /></h1>
          <p className="text-sm text-navy-500 mt-1">Define and monitor conflicting access policies</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCheckAll} disabled={checking} variant="outline" className="rounded-sm">
            {checking ? <ArrowsClockwise size={14} className="mr-1 animate-spin" /> : <Play size={14} className="mr-1" />}
            Check All Policies
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm">
            <Plus size={14} className="mr-1" /> New Policy
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={ShieldCheck} label="Active Policies" value={policies.filter(p => p.enabled).length} />
        <StatCard icon={Warning} label="Open Violations" value={violations.length} accent />
        <StatCard icon={XCircle} label="High Severity" value={violations.filter(v => v.severity === 'high').length} color="red" />
        <StatCard icon={CheckCircle} label="Compliant Policies" value={policies.filter(p => p.violation_count === 0).length} color="emerald" />
      </div>

      <Tabs defaultValue="policies" className="space-y-4">
        <TabsList className="bg-slate-100 rounded-sm p-0.5 h-auto">
          <TabsTrigger value="policies" className="rounded-sm text-xs px-4 py-2">Policies ({policies.length})</TabsTrigger>
          <TabsTrigger value="violations" className="rounded-sm text-xs px-4 py-2">
            Open Violations ({violations.length})
            {violations.length > 0 && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full inline-block"></span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="policies">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="h-1.5 bg-navy-600" />
            <Table wrapperClassName="max-h-[calc(100vh-380px)]">
              <TableHeader>
                <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Policy Name</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Conflicts</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Severity</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Enforcement</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Violations</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {policies.length === 0 ? (
                  <TableRow><TableCell colSpan={7}><EmptyState type="sod" onAction={() => setShowCreateDialog(true)} /></TableCell></TableRow>
                ) : policies.map(policy => (
                  <TableRow key={policy.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="font-medium text-sm text-navy-900">{policy.name}</div>
                      <div className="text-xs text-navy-400">{policy.description || 'No description'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {policy.conflict_details?.map((c, i) => (
                          <div key={`${c.item1_name}-${c.item2_name}`} className="flex items-center gap-1 text-xs">
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{c.item1_name}</span>
                            <XCircle size={10} className="text-red-400" />
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{c.item2_name}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${severityColor(policy.severity)}`}>
                        {policy.severity}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-navy-600 capitalize">{policy.enforcement}</TableCell>
                    <TableCell>
                      <span className={`font-semibold ${policy.violation_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {policy.violation_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${policy.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-500'}`}>
                        {policy.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(policy)} className="text-navy-500 hover:text-navy-700 h-7" data-testid={`edit-policy-${policy.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(policy.id)} className="text-red-500 hover:text-red-700 h-7" data-testid={`delete-policy-${policy.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="violations">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <Table wrapperClassName="max-h-[calc(100vh-380px)]">
              <TableHeader>
                <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Identity</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Conflicting Access</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Policy</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Severity</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Detected</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {violations.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-sm text-navy-400">
                    <CheckCircle size={32} className="mx-auto mb-2 text-emerald-300" />
                    No open violations. Great job!
                  </TableCell></TableRow>
                ) : violations.map(v => (
                  <TableRow key={v.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-sm text-navy-900">{v.identity_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-xs">
                        <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{v.item1_name}</span>
                        <XCircle size={10} className="text-red-400" />
                        <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{v.item2_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-navy-600">{v.policy_name}</TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${severityColor(v.severity)}`}>
                        {v.severity}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-navy-500">{new Date(v.detected_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => openResolveDialog(v)} className="bg-orange-500 hover:bg-orange-600 text-white h-7 text-xs rounded-sm">
                        Resolve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Policy Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Create SoD Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Policy Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" placeholder="e.g., Finance Segregation" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({...form, severity: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Enforcement</Label>
                <Select value={form.enforcement} onValueChange={(v) => setForm({...form, enforcement: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="block">Block</SelectItem>
                    <SelectItem value="warn">Warn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Conflicting Roles *</Label>
              <div className="flex gap-2 mt-2">
                <Select value={conflictPair.item1} onValueChange={(v) => setConflictPair({...conflictPair, item1: v})}>
                  <SelectTrigger className="rounded-sm flex-1"><SelectValue placeholder="Role A" /></SelectTrigger>
                  <SelectContent>
                    {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <XCircle size={20} className="text-red-400 self-center" />
                <Select value={conflictPair.item2} onValueChange={(v) => setConflictPair({...conflictPair, item2: v})}>
                  <SelectTrigger className="rounded-sm flex-1"><SelectValue placeholder="Role B" /></SelectTrigger>
                  <SelectContent>
                    {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={addConflictPair} variant="outline" size="sm" className="rounded-sm">
                  <Plus size={14} />
                </Button>
              </div>
              {form.conflict_items.length > 0 && (
                <div className="mt-3 space-y-2">
                  {form.conflict_items.map((pair, i) => (
                    <div key={`${pair[0]}-${pair[1]}`} className="flex items-center gap-2 bg-slate-50 p-2 rounded-sm">
                      <span className="text-sm flex-1">{getRoleName(pair[0])} <XCircle size={12} className="inline text-red-400" /> {getRoleName(pair[1])}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeConflictPair(i)} className="h-6 w-6 p-0 text-red-500">
                        <Trash size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreate} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm">Create Policy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Policy Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Edit SoD Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Policy Name *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="mt-1 rounded-sm" placeholder="e.g., Finance Segregation" data-testid="edit-policy-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
              <Input value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="mt-1 rounded-sm" data-testid="edit-policy-description" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Severity</Label>
                <Select value={editForm.severity} onValueChange={(v) => setEditForm({...editForm, severity: v})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-policy-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Enforcement</Label>
                <Select value={editForm.enforcement} onValueChange={(v) => setEditForm({...editForm, enforcement: v})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-policy-enforcement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="block">Block</SelectItem>
                    <SelectItem value="warn">Warn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Status</Label>
                <div className="mt-2 flex items-center gap-2">
                  <Switch 
                    checked={editForm.enabled} 
                    onCheckedChange={(checked) => setEditForm({...editForm, enabled: checked})}
                    data-testid="edit-policy-enabled"
                  />
                  <span className={`text-sm ${editForm.enabled ? 'text-emerald-600 font-medium' : 'text-navy-400'}`}>
                    {editForm.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Conflicting Roles *</Label>
              <div className="flex gap-2 mt-2">
                <Select value={editConflictPair.item1} onValueChange={(v) => setEditConflictPair({...editConflictPair, item1: v})}>
                  <SelectTrigger className="rounded-sm flex-1" data-testid="edit-conflict-role1"><SelectValue placeholder="Role A" /></SelectTrigger>
                  <SelectContent>
                    {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <XCircle size={20} className="text-red-400 self-center" />
                <Select value={editConflictPair.item2} onValueChange={(v) => setEditConflictPair({...editConflictPair, item2: v})}>
                  <SelectTrigger className="rounded-sm flex-1" data-testid="edit-conflict-role2"><SelectValue placeholder="Role B" /></SelectTrigger>
                  <SelectContent>
                    {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={addEditConflictPair} variant="outline" size="sm" className="rounded-sm" data-testid="add-edit-conflict-pair">
                  <Plus size={14} />
                </Button>
              </div>
              {editForm.conflict_items.length > 0 && (
                <div className="mt-3 space-y-2 max-h-[150px] overflow-y-auto">
                  {editForm.conflict_items.map((pair, i) => (
                    <div key={`${pair[0]}-${pair[1]}`} className="flex items-center gap-2 bg-slate-50 p-2 rounded-sm">
                      <span className="text-sm flex-1">{getRoleName(pair[0])} <XCircle size={12} className="inline text-red-400" /> {getRoleName(pair[1])}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeEditConflictPair(i)} className="h-6 w-6 p-0 text-red-500" data-testid={`remove-edit-conflict-${i}`}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {editForm.conflict_items.length === 0 && (
                <p className="text-xs text-red-500 mt-2">At least one conflict pair is required</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleEdit} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-policy-edit">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Violation Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Resolve Violation</DialogTitle>
          </DialogHeader>
          {selectedViolation && (
            <div className="space-y-4 py-2">
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <p className="text-sm font-medium text-red-800">{selectedViolation.identity_name}</p>
                <p className="text-xs text-red-600 mt-1">
                  Has both: <span className="font-semibold">{selectedViolation.item1_name}</span> and <span className="font-semibold">{selectedViolation.item2_name}</span>
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Resolution</Label>
                <Select value={resolveForm.resolution} onValueChange={(v) => setResolveForm({...resolveForm, resolution: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accepted">Accept Risk</SelectItem>
                    <SelectItem value="remediated">Remediated (Access Removed)</SelectItem>
                    <SelectItem value="exception">Exception Granted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Notes</Label>
                <Textarea 
                  value={resolveForm.notes} 
                  onChange={(e) => setResolveForm({...resolveForm, notes: e.target.value})} 
                  className="mt-1 rounded-sm" 
                  rows={3}
                  placeholder="Justification for resolution..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleResolve} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm">Resolve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, color }) {
  const colorClasses = color === 'red' ? 'bg-red-50 border-red-200' : 
                       color === 'emerald' ? 'bg-emerald-50 border-emerald-200' :
                       accent ? 'bg-orange-50 border-orange-200' : 'bg-white border-slate-200';
  const iconClasses = color === 'red' ? 'bg-red-100 text-red-600' :
                      color === 'emerald' ? 'bg-emerald-100 text-emerald-600' :
                      accent ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-navy-500';

  return (
    <div className={`rounded-sm border p-4 ${colorClasses}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-sm ${iconClasses}`}>
          <Icon size={18} />
        </div>
        <div>
          <p className="text-2xl font-bold text-navy-900">{value}</p>
          <p className="text-xs text-navy-500 uppercase tracking-wider">{label}</p>
        </div>
      </div>
    </div>
  );
}
